import { CustomerModel } from '../customer.model';

/**
 * The two named customer lookups — account number and customer name — shared by every screen
 * that offers them (the Customers page and step 1 of Collect Payment), so "which customers does
 * this term find" has one answer everywhere.
 *
 * These are deliberately separate from the grid's own "Search" box, which matches a term against
 * every column at once. The two things people actually arrive knowing are an account number and a
 * company name, and one box cannot serve both: an account number like `1050` also appears inside
 * phone numbers and invoice references, so a combined search returns the right customer buried in
 * rows that merely contain the digits.
 */
export interface CustomerLookupTerms {
  /** Normalized (see `normalizeLookupTerm`); empty means "not filtering on this". */
  accountNumber: string;
  customerName: string;
}

/** Trimmed + lowercased, so a trailing space from a paste is not a failed match. */
export function normalizeLookupTerm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Customers matching both lookups.
 *
 * They combine with AND — filling in both means "this account, and this name" — and each is a
 * case-insensitive substring match, matching how the grid's own search and the server-side
 * `GET /customers?search=` both behave. The name arm reads `companyName` **or** `displayName`:
 * the grid column shows only the former, but a customer carrying just a display name would
 * otherwise be unfindable by the name most of the app labels them with.
 */
export function filterCustomersByLookup(customers: CustomerModel[], terms: CustomerLookupTerms): CustomerModel[] {
  const { accountNumber, customerName } = terms;
  if (!accountNumber && !customerName) return customers;

  return customers.filter((customer) => {
    const matchesAccount =
      !accountNumber || (customer.companyAccountNumber ?? '').toLowerCase().includes(accountNumber);
    const matchesName =
      !customerName ||
      (customer.companyName ?? '').toLowerCase().includes(customerName) ||
      (customer.displayName ?? '').toLowerCase().includes(customerName);
    return matchesAccount && matchesName;
  });
}

/**
 * "Account Number - Customer Name" — the Customers grid's leading column. The name is
 * `companyName`, falling back to `displayName` like the name lookup above; a missing half is
 * left out rather than shown as a dangling dash.
 */
export function customerAccountLabel(customer: CustomerModel): string {
  const account = (customer.companyAccountNumber ?? '').trim();
  const name = (customer.companyName || customer.displayName || '').trim();
  if (account && name) return `${account} - ${name}`;
  return account || name;
}
